import React, { useEffect, useState } from 'react';
import { FileText, Image as ImageIcon, Trash2, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DocumentUploadForm, { DocumentUploadMeta } from '@/components/PatientDocuments/DocumentUploadForm';
import TypeBadge from '@/components/PatientDocuments/TypeBadge';

export interface StagedAttachment {
  id: string;
  files: File[];
  meta: DocumentUploadMeta;
}

interface PendingAttachmentsProps {
  onChange: (items: StagedAttachment[]) => void;
}

function makeLocalId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
}

// A single staged entry (a set of files sharing one type/date/body-site).
// Thumbnails use URL.createObjectURL directly — the files are already local
// File objects in memory, no server round-trip needed to preview them (unlike
// PatientDocuments' PhotoThumbnail, which fetches an already-uploaded file
// from the API and therefore needs an authenticated blob fetch).
function StagedEntryPreview({ item, onRemove }: { item: StagedAttachment; onRemove: () => void }) {
  const [previewUrls, setPreviewUrls] = useState<(string | null)[]>([]);

  useEffect(() => {
    const urls = item.files.map((f) => (f.type.startsWith('image/') ? URL.createObjectURL(f) : null));
    setPreviewUrls(urls);
    return () => { urls.forEach((u) => u && URL.revokeObjectURL(u)); };
  }, [item.files]);

  return (
    <div className="flex items-center gap-2.5 rounded-md border border-info/20 bg-info/5 px-2.5 py-2 text-xs animate-fade-in">
      <div className="flex -space-x-2 flex-shrink-0">
        {item.files.slice(0, 3).map((f, idx) => (
          <div key={idx} className="w-7 h-7 rounded-md border-2 border-card bg-muted overflow-hidden flex items-center justify-center">
            {previewUrls[idx] ? (
              <img src={previewUrls[idx]!} alt={f.name} className="w-full h-full object-cover" />
            ) : (
              <FileText className="w-3 h-3 text-muted-foreground" />
            )}
          </div>
        ))}
      </div>

      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className="truncate font-medium">
          {item.files.length === 1 ? item.files[0].name : `${item.files.length} files`}
        </span>
        <TypeBadge type={item.meta.documentType} />
        <span className="text-muted-foreground flex-shrink-0 tabular-nums">{item.meta.documentDate}</span>
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 flex-shrink-0 hover:bg-destructive/10 hover:text-destructive"
        onClick={onRemove}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

// Staging area for attachments picked before a prescription has been saved
// (and therefore has no id yet to link a real upload to). Files stay local
// until the parent form saves successfully and flushes them with the new
// prescription_id — see PrescriptionForm.tsx / usePrescriptionForm.ts.
export default function PendingAttachments({ onChange }: PendingAttachmentsProps) {
  const [items, setItems] = useState<StagedAttachment[]>([]);

  const stageFiles = (files: File[], meta: DocumentUploadMeta) => {
    setItems((prev) => {
      const next = [...prev, { id: makeLocalId(), files, meta }];
      onChange(next);
      return next;
    });
  };

  const removeItem = (id: string) => {
    setItems((prev) => {
      const next = prev.filter((i) => i.id !== id);
      onChange(next);
      return next;
    });
  };

  return (
    <div className="space-y-3">
      <DocumentUploadForm onSubmit={stageFiles} submitLabel="Add" title="Add a document" />

      {items.length > 0 && (
        <div className="space-y-1.5">
          {items.map((item) => (
            <StagedEntryPreview key={item.id} item={item} onRemove={() => removeItem(item.id)} />
          ))}
        </div>
      )}

      <div className="flex items-start gap-1.5 text-[10px] text-muted-foreground">
        <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
        <span>{items.length > 0 ? `${items.length} document${items.length > 1 ? 's' : ''} will upload once you save this prescription.` : 'Files are attached automatically when you save this prescription.'}</span>
      </div>
    </div>
  );
}
